import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loginSSO } from '../../api/auth.api';
import Logger from '../../utils/logger';
import LOGO from '../../assets/LOGO (2).png';

const SSOCallback = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(3);
  const [isExiting, setIsExiting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasRun = useRef(false);
  const countdownRef = useRef(null);

  useEffect(() => {
    if (hasRun.current) return;

    const handleCallback = async () => {
      try {
        const easySuiteToken = searchParams.get('token');
        if (!easySuiteToken) {
          throw new Error("Token manquant dans l'URL");
        }

        Logger.log('Token Easy Suite reçu');

        hasRun.current = true;
        searchParams.delete('token');
        setSearchParams(searchParams, { replace: true });

        const response = await loginSSO(easySuiteToken);

        if (!response.success) {
          throw new Error(response.message || "Erreur d'authentification");
        }

        Logger.log('Authentification SSO réussie');
        setIsExiting(true);
        setTimeout(() => {
          navigate('/admin', { replace: true });
        }, 500);
      } catch (err) {
        Logger.error('Erreur SSO:', err);
        setError(err.message || "Erreur d'authentification");
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, setSearchParams, navigate]);

  // Countdown timer for error state
  useEffect(() => {
    if (!error || countdown <= 0) return;

    countdownRef.current = setTimeout(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          setIsExiting(true);
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 500);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearTimeout(countdownRef.current);
  }, [error, countdown, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Card Container */}
          <div className={`bg-white rounded-2xl shadow-2xl p-8 space-y-8 transition-all duration-500 ${
            isExiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
          }`}>
            {/* Logo/Header */}
            <div className="text-center space-y-4">
              <img src={LOGO} alt="Easy Survey" className="mx-auto h-20 w-auto" />
              <div>
                {/* <h1 className="text-2xl font-bold text-gray-900">Easy Survey</h1> */}
                <p className="text-sm text-gray-500 mt-1">Connexion sécurisée en cours</p>
              </div>
            </div>

            {/* Loading Animation */}
            <div className="flex justify-center">
              <div className="relative w-12 h-12">
                <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-teal-700 rounded-full animate-spin" style={{
                  maskImage: 'radial-gradient(circle, transparent 30%, black 70%)',
                  WebkitMaskImage: 'radial-gradient(circle, transparent 30%, black 70%)'
                }}></div>
                <div className="absolute inset-1 bg-white rounded-full"></div>
              </div>
            </div>

            {/* Status Text */}
            <div className="space-y-2 text-center">
              <p className="text-gray-700 font-medium">Vérification de votre identité</p>
              <p className="text-sm text-gray-500">Veuillez patienter quelques secondes...</p>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-teal-600 to-teal-700 rounded-full animate-pulse"></div>
            </div>

            {/* Footer Message */}
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400 text-center">
                Vous serez redirigé automatiquement vers votre tableau de bord
              </p>
            </div>
          </div>

          {/* Trust Badge */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
              <svg className="w-4 h-4 text-teal-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Connexion sécurisée</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4 transition-all duration-500 ${
        isExiting ? 'opacity-0' : 'opacity-100'
      }`}>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-6">
            {/* Error Icon */}
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>

            {/* Error Message */}
            <div className="text-center space-y-2">
              <h2 className="text-xl font-bold text-gray-900">Erreur d'authentification</h2>
              <p className="text-sm text-gray-600">{error}</p>
            </div>

            {/* Redirect Info with Countdown */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <p className="text-sm text-teal-800 text-center">
                Redirection dans <span className="font-bold text-teal-700">{countdown}s</span>
              </p>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  window.location.href = 'https://easysuite.bakeli.tech/applications';
                }}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200"
              >
                Retour à Easy Suite
              </button>
              <button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  setCountdown(3);
                  hasRun.current = false;
                  window.location.reload();
                }}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2.5 px-4 rounded-lg transition-colors duration-200"
              >
                Réessayer
              </button>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400 text-center">
                Si le problème persiste, veuillez contacter le support
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SSOCallback;
